import React, { useEffect, useState } from "react";
import { Button, Input, Segmented, Select, Space, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { searchArticles } from "./api";
import { KIND_COLORS, formatEuro, kindLabels } from "./format";
import ArticleEditor from "./ArticleEditor";

const PAGE_SIZE = 25;

/** The catalog list: prefix search over 100k+ articles, paged on the server. */
const CatalogTab = ({ meta }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const kinds = kindLabels(t);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [supplier, setSupplier] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await searchArticles({
        q: query,
        kind: kind === "all" ? undefined : kind,
        supplier: supplier === "all" ? undefined : supplier,
        includeInactive: showInactive,
        page,
        pageSize: PAGE_SIZE
      });
      setResult(res.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [query, kind, supplier, showInactive, page]);

  const columns = [
    {
      title: t("Article"),
      key: "name",
      mobile: "title",
      render: (_, a) => (
        <span className="mat-wrap">
          {a.name}
          {a.shortText2 ? <span className="mat-muted"> · {a.shortText2}</span> : null}
          {!a.active ? <Tag className="mat-inactive">{t("Inactive")}</Tag> : null}
        </span>
      )
    },
    {
      title: t("Article no."),
      key: "articleNumber",
      mobile: "subtitle",
      render: (_, a) => [a.articleNumber, a.supplierName].filter(Boolean).join(" · ")
    },
    {
      title: t("Kind"),
      key: "kind",
      mobile: "badge",
      render: (_, a) => <Tag color={KIND_COLORS[a.kind]}>{kinds[a.kind]}</Tag>
    },
    { title: t("Unit"), dataIndex: "unit", key: "unit" },
    {
      title: t("Purchase price"),
      key: "purchase",
      align: "right",
      render: (_, a) =>
        a.kind === "article" ? (
          <span className="mat-number">
            {formatEuro(a.netPurchasePrice, lng)}
            {a.priceUnit > 1 ? ` / ${a.priceUnit}` : ""}
          </span>
        ) : (
          "–"
        )
    },
    {
      title: t("Sales price"),
      key: "price",
      align: "right",
      render: (_, a) => <span className="mat-number">{formatEuro(a.unitPrice, lng)}</span>
    },
    {
      title: t("Actions"),
      key: "actions",
      render: (_, a) => (
        <Button size="small" onClick={() => setEditing(a._id)}>
          {meta.permissions.update ? t("Edit") : t("Open")}
        </Button>
      )
    }
  ];

  return (
    <div>
      <PageToolbar
        filters={
          <Space direction="vertical" className="mat-full">
            <Input.Search
              allowClear
              placeholder={t("Search by name, article number or EAN")}
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            <Space wrap>
              <Segmented
                value={kind}
                onChange={value => {
                  setKind(value);
                  setPage(1);
                }}
                options={[
                  { value: "all", label: t("All") },
                  { value: "article", label: kinds.article },
                  { value: "labour", label: kinds.labour },
                  { value: "assembly", label: kinds.assembly }
                ]}
              />
              <Select
                value={supplier}
                onChange={value => {
                  setSupplier(value);
                  setPage(1);
                }}
                popupMatchSelectWidth={false}
                options={[
                  { value: "all", label: t("All suppliers") },
                  { value: "own", label: t("Own articles") },
                  ...meta.suppliers.map(s => ({ value: s._id, label: s.name }))
                ]}
              />
              <Segmented
                value={showInactive ? "all" : "active"}
                onChange={value => setShowInactive(value === "all")}
                options={[
                  { value: "active", label: t("Active") },
                  { value: "all", label: t("Including inactive") }
                ]}
              />
            </Space>
          </Space>
        }
        actions={
          meta.permissions.create ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
              {t("New article")}
            </Button>
          ) : null
        }
      />
      {result.anywhere ? (
        <Typography.Paragraph className="mat-muted">
          {t("No word starts with your search; showing articles that contain it.")}
        </Typography.Paragraph>
      ) : null}
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={result.data}
        mobileAvatar={false}
        onRow={record => ({ onDoubleClick: () => setEditing(record._id) })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: result.total,
          showSizeChanger: false,
          onChange: setPage
        }}
        scroll={{ x: 760 }}
      />
      <ArticleEditor
        meta={meta}
        articleId={editing}
        onClose={() => setEditing(null)}
        onSaved={() => load()}
      />
    </div>
  );
};

export default CatalogTab;
